import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { Pool } from '../game/RelicPool';
import { TOTEMS, type TotemKind, type Totems } from '../game/Totems';
import type { Board } from '../game/Board';
import { FOG_FRAG, FOG_VERT, fxUniforms } from '../fx/common';
import { createOrbMaterial } from '../fx/orbMaterial';
import { Shape } from '../fx/Particles';
import type { Fx } from '../fx/Fx';
import { hash } from '../engine/noise';
import { RelicPool } from '../game/RelicPool';
import type { WorldCtx, WorldInfo } from './World';

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler(), _p = new THREE.Vector3(), _s = new THREE.Vector3();
const _c = new THREE.Color(), _hit = new THREE.Vector3();
const TOTEM_KINDS = ['wind', 'might', 'ascend', 'shatter'] as const;
export const SHORT: Record<TotemKind, string> = { wind: 'Вітер', might: 'Сила', ascend: 'Піднесення', shatter: 'Знищення' };
const GLYPH: Record<TotemKind, string> = { wind: '≋', might: '◆', ascend: '⇑', shatter: '✕' };
const BRIEF: Record<TotemKind, string> = { wind: '+15% швидкості атак', might: '+20% шкоди сфер', ascend: `Найслабша сфера +1 рівень раз на ${TOTEMS.ascend.period} с`, shatter: `Розбити будь-яку сферу раз на ${TOTEMS.shatter.period} с` };
/** Charge ladder of the ascend totem: five rune rungs up its tiers (y, front z, width). */
const RUNGS = [[0.3, 0.205, 0.16], [0.43, 0.205, 0.16], [0.63, 0.165, 0.13], [0.76, 0.165, 0.13], [0.96, 0.125, 0.1]] as const;
/** Living heart of each totem: the same glass-orb shader as the relics, element id per type. */
const HEART: Record<TotemKind, { element: number; y: number; r: number }> = { wind: { element: 9, y: 1.5, r: 0.17 }, might: { element: 0, y: 1.32, r: 0.19 }, ascend: { element: 10, y: 1.42, r: 0.16 }, shatter: { element: 8, y: 1.3, r: 0.17 } };
type View = { loadout: Totems; kind: TotemKind | null; badge?: string; bounds: THREE.Box3; focus: number; reveal: number; version: number; light: number; ground: number };

/** A feather / leaf outline: pointed tip up, length `l`, half-width `w`. */
function leaf(l: number, w: number) {
  const sh = new THREE.Shape(); sh.moveTo(0, 0);
  sh.quadraticCurveTo(w, l * 0.35, 0, l); sh.quadraticCurveTo(-w, l * 0.35, 0, 0);
  return sh;
}

/** One equipped artifact per board. Models are built once, merged by material and shared between owners. */
export class TotemRack {
  readonly buttons: HTMLButtonElement[] = [];
  /** Each kind's model in local space, one merged geometry per material. */
  private readonly bodies: Record<TotemKind, Map<THREE.Material, THREE.BufferGeometry>>;
  /** Both owners' models baked into one static mesh per material, rebuilt only when a totem changes. */
  private readonly statics = new Map<THREE.Material, THREE.Mesh>();
  private energy!: THREE.ShaderMaterial;
  private organic!: THREE.MeshStandardMaterial;
  private readonly cores: Pool;
  private bone!: THREE.MeshStandardMaterial;
  private stone!: THREE.MeshStandardMaterial;
  private feather!: THREE.MeshStandardMaterial;
  private ribbon!: THREE.MeshStandardMaterial;
  private emberT = 0;
  private readonly vanes: Pool;
  private readonly motes: Pool;
  private readonly rungs: Pool;
  private moteT = 0;
  private readonly views: View[] = [];
  private readonly choices: HTMLButtonElement[] = [];
  private readonly chooser: HTMLDivElement;
  private hover = -1;
  private shadowsChanged = true;
  private width = innerWidth; private height = innerHeight;

  constructor(private readonly ctx: WorldCtx, private readonly world: WorldInfo, private readonly board: Board, host: HTMLElement, private readonly fx?: Fx) {
    const energy = this.energy = new THREE.ShaderMaterial({
      uniforms: fxUniforms, vertexColors: true,
      vertexShader: /* glsl */ `
        varying vec3 vColor, vN, vLocal;
        ${FOG_VERT}
        void main() {
          // the same runes material serves the instanced pools (wisps, rungs) and the static rune carvings
          #ifdef USE_INSTANCING
            mat4 local = instanceMatrix;
          #else
            mat4 local = mat4(1.0);
          #endif
          #ifdef USE_INSTANCING_COLOR
            vColor = instanceColor;
          #else
            vColor = color;
          #endif
          vec4 world = modelMatrix * local * vec4(position, 1.0);
          vN = normalize(mat3(modelMatrix * local) * normal); vLocal = position;
          vec4 mv = viewMatrix * world; FOG_FROM_MV(mv)
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime; varying vec3 vColor, vN, vLocal;
        ${FOG_FRAG}
        float h(vec3 p) { return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453); }
        void main() {
          // runes cut into stone: light seeps from deep in the groove, unevenly, and breathes slowly
          float grain = 0.55 + 0.45 * h(floor(vLocal * 60.0));
          float breathe = 0.72 + 0.28 * sin(uTime * 1.7 + vLocal.y * 9.0 + vLocal.x * 5.0);
          vec3 c = vColor * 0.75 * grain * breathe + vColor * vColor * 0.25;
          gl_FragColor = vec4(c * fogKeep(), 1.0);
        }`,
    });
    // bone, feathers and ribbons are one material (colour per vertex, ribbons sway by aSway): one draw
    this.organic = this.swayCloth(ctx.tex.stone.bump);
    this.bone = new THREE.MeshStandardMaterial({ name: 'totemBone', color: 0xd6c9ad });
    this.stone = new THREE.MeshStandardMaterial({ name: 'totemStone', map: ctx.tex.stone.map, bumpMap: ctx.tex.stone.bump, bumpScale: 2.2, color: 0xc4bcae, roughness: 0.9, flatShading: true });
    this.feather = new THREE.MeshStandardMaterial({ name: 'totemFeather', color: 0xcfc6b2, roughness: 0.85, side: THREE.DoubleSide });
    this.ribbon = new THREE.MeshStandardMaterial({ name: 'totemRibbon', color: 0x8fd6cf });
    this.bodies = { wind: this.buildBody('wind', energy), might: this.buildBody('might', energy), ascend: this.buildBody('ascend', energy), shatter: this.buildBody('shatter', energy) };
    for (const mat of new Set(Object.values(this.bodies).flatMap(b => [...b.keys()]))) {
      const mesh = new THREE.Mesh(new THREE.BufferGeometry(), mat); mesh.name = `totemBody-${mat === energy ? 'runes' : mat.name}`;
      mesh.castShadow = mat !== energy; mesh.receiveShadow = true; mesh.matrixAutoUpdate = false; mesh.visible = false;
      this.statics.set(mat, mesh); ctx.scene.add(mesh);
    }
    // the heart: a small living orb, the same glass and inner element as the relics on the board
    this.cores = new Pool(new THREE.IcosahedronGeometry(1, 3), createOrbMaterial({ volumeSteps: 4 }), 2, { iColor: 3, iGem: 4, iDetail: 2 });
    this.cores.mesh.name = 'totemHearts'; this.cores.mesh.castShadow = false;
    // wind: faint spiral wisps of air round the crown (thin, translucent-looking, slow)
    this.vanes = new Pool(new THREE.TorusGeometry(1, 0.006, 3, 48, Math.PI * 1.2), energy, 4);
    this.vanes.mesh.name = 'totemWisps'; this.vanes.mesh.castShadow = false;
    // drifting feathers (wind) or lifting embers (might)
    const featherGeo = new THREE.ShapeGeometry(leaf(1, 0.32), 4);
    this.motes = new Pool(featherGeo, this.feather, 6);
    this.motes.mesh.name = 'totemMotes'; this.motes.mesh.castShadow = false;
    this.rungs = new Pool(new THREE.BoxGeometry(1, 0.024, 0.014), energy, RUNGS.length * 2);
    this.rungs.mesh.name = 'totemRungs'; this.rungs.mesh.castShadow = false;
    ctx.scene.add(this.cores.mesh, this.vanes.mesh, this.motes.mesh, this.rungs.mesh);

    const bar = document.createElement('div'); bar.className = 'totem-rack'; bar.setAttribute('aria-label', 'Тотеми гравців');
    world.totemSockets.forEach((socket, i) => {
      const loadout = i === 0 ? board.totems : board.partnerTotems!;
      this.views.push({ loadout, kind: null, focus: 0, reveal: 0, version: -1,
        bounds: new THREE.Box3(new THREE.Vector3(socket.x - 0.55, socket.y, socket.z - 0.48), new THREE.Vector3(socket.x + 0.55, socket.y + 1.85, socket.z + 0.48)),
        light: ctx.glows.add(TOTEMS[loadout.kind].color, socket.x, socket.y + 1.19, socket.z, { size: 1.05, opacity: 0.3 }),
        ground: ctx.floorGlows.add(TOTEMS[loadout.kind].color, socket.x, socket.y + 0.025, socket.z, { size: 1.08, opacity: 0.22 }),
      });
      this.cores.alloc(); for (let k = 0; k < 2; k++) this.vanes.alloc(); for (let k = 0; k < 3; k++) this.motes.alloc(); for (let k = 0; k < RUNGS.length; k++) this.rungs.alloc();
      const button = document.createElement('button'); button.className = 'totem-label'; button.type = 'button'; button.dataset.owner = socket.owner;
      if (i === 0) { button.setAttribute('aria-expanded', 'false'); button.setAttribute('aria-controls', 'totem-choice'); }
      for (const [cls, content] of [['totem-owner', i ? 'СОЮЗНИК' : 'ВАШ ТОТЕМ'], ['totem-name', ''], ['totem-badge', '']]) {
        const span = document.createElement('span'); span.className = cls; span.textContent = content; button.append(span);
      }
      button.addEventListener('pointerdown', e => e.stopPropagation());
      button.addEventListener('click', e => { e.stopPropagation(); this.activate(i); });
      button.addEventListener('pointerenter', () => this.hover = i); button.addEventListener('pointerleave', () => this.hover = -1);
      button.addEventListener('focus', () => this.hover = i); button.addEventListener('blur', () => this.hover = -1);
      this.buttons.push(button); bar.append(button);
    });

    this.chooser = document.createElement('div'); this.chooser.id = 'totem-choice'; this.chooser.className = 'totem-choice'; this.chooser.hidden = true;
    this.chooser.setAttribute('role', 'group'); this.chooser.setAttribute('aria-label', 'Вибір одного активного тотема');
    const heading = document.createElement('div'); heading.className = 'totem-choice-heading'; heading.textContent = 'Один активний тотем';
    const close = document.createElement('button'); close.type = 'button'; close.className = 'totem-close'; close.textContent = '×'; close.setAttribute('aria-label', 'Закрити вибір тотема');
    close.addEventListener('click', () => this.close(true)); heading.append(close); this.chooser.append(heading);
    for (const kind of TOTEM_KINDS) {
      const spec = TOTEMS[kind], button = document.createElement('button'); button.type = 'button'; button.className = 'totem-option'; button.dataset.kind = kind;
      button.style.setProperty('--totem', `#${spec.color.toString(16).padStart(6, '0')}`);
      const glyph = document.createElement('span'); glyph.className = 'totem-option-icon'; glyph.textContent = GLYPH[kind]; glyph.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span'), name = document.createElement('strong'), detail = document.createElement('small'); name.textContent = spec.name; detail.textContent = BRIEF[kind]; text.append(name, detail);
      const mark = document.createElement('span'); mark.className = 'totem-option-mark'; mark.setAttribute('aria-hidden', 'true');
      button.append(glyph, text, mark); button.addEventListener('click', () => { board.equipTotem(kind); this.syncChoices(); this.close(true); });
      this.choices.push(button); this.chooser.append(button);
    }
    const hint = document.createElement('p'); hint.textContent = 'Заміна прибирає бонус попереднього тотема.'; this.chooser.append(hint);
    this.chooser.addEventListener('pointerdown', e => e.stopPropagation()); this.chooser.addEventListener('click', e => e.stopPropagation());
    bar.append(this.chooser); host.append(bar);
    // the field's own box: in Telegram it is inset from the window by the safe areas
    const size = () => { this.width = host.clientWidth || innerWidth; this.height = host.clientHeight || innerHeight; };
    size(); new ResizeObserver(size).observe(host);
    window.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(true); });
  }

  /** Bone, feather and ribbon in one material: colour per vertex; ribbons flutter by their aSway weight (0 at the knot, 1 at the tail). */
  private swayCloth(bump: THREE.Texture) {
    const m = new THREE.MeshStandardMaterial({ name: 'totemOrganic', vertexColors: true, roughness: 0.8, bumpMap: bump, bumpScale: 0.5, side: THREE.DoubleSide });
    m.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = fxUniforms.uTime;
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime; attribute float aSway;')
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          float w = aSway * aSway;
          transformed.x += (sin(uTime * 2.6 + position.y * 7.0 + position.z * 3.0) * 0.05 + 0.03) * w;
          transformed.z += sin(uTime * 1.9 + position.y * 5.0 + position.x * 4.0) * 0.04 * w;`);
    };
    m.customProgramCacheKey = () => 'totem-organic-sway';
    return m;
  }

  /**
   * An ancient standing stone, hewn rough, bound in bronze, its runes cut deep and glowing from within;
   * each kind is crowned differently — wind with bone feathers, bronze talons and fluttering ribbons,
   * might with a heavy iron-banded block, ram horns of bone and a brazier holding a fire heart.
   */
  private buildBody(kind: TotemKind, energy: THREE.Material): Map<THREE.Material, THREE.BufferGeometry> {
    const parts = new Map<THREE.Material, THREE.BufferGeometry[]>();
    const add = (g: THREE.BufferGeometry, mat: THREE.Material, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0) => {
      g.rotateX(rx).rotateY(ry).rotateZ(rz).translate(x, y, z);
      const flat = g.index ? g.toNonIndexed() : g; if (flat !== g) g.dispose();
      const n = flat.attributes.position.count;
      if (!flat.getAttribute('uv')) flat.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
      if (mat === this.bone || mat === this.feather || mat === this.ribbon) {
        // marker materials: bake the look into the shared organic material
        const c = (mat as THREE.MeshStandardMaterial).color, col = new Float32Array(n * 3);
        for (let i = 0; i < n; i++) col.set([c.r, c.g, c.b], i * 3);
        flat.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
        if (!flat.getAttribute('aSway')) flat.setAttribute('aSway', new THREE.Float32BufferAttribute(new Float32Array(n), 1));
        mat = this.organic;
      }
      let list = parts.get(mat); if (!list) parts.set(mat, list = []); list.push(flat);
    };
    const { M } = this.ctx, wind = kind === 'wind', seed = wind ? 3 : 7;
    /** Rough-hewn: every vertex nudged by a stable hash, then faceted (flat normals). */
    const hewn = (g: THREE.BufferGeometry, amount: number, salt: number) => {
      const flat = g.index ? g.toNonIndexed() : g, p = flat.attributes.position;
      for (let i = 0; i < p.count; i++) {
        const x = p.getX(i), y = p.getY(i), z = p.getZ(i), k = Math.round(x * 97) * 31 + Math.round(y * 89) * 17 + Math.round(z * 83);
        p.setXYZ(i, x + (hash(k, salt, 1) - 0.5) * amount, y + (hash(k, salt, 2) - 0.5) * amount * 0.5, z + (hash(k, salt, 3) - 0.5) * amount);
      }
      flat.computeVertexNormals(); return flat;
    };
    const ring = (r: number, tube: number, y: number, mat: THREE.Material) => add(new THREE.TorusGeometry(r, tube, 5, 28), mat, 0, y, 0, Math.PI / 2);
    const tube = (pts: number[][], r0: number, r1: number, mat: THREE.Material, segs = 14) => {
      const curve = new THREE.CatmullRomCurve3(pts.map(([x, y, z]) => new THREE.Vector3(x, y, z)));
      const g = new THREE.TubeGeometry(curve, segs, 1, 6), p = g.attributes.position;
      // taper: rescale each ring around its point on the curve
      for (let i = 0; i <= segs; i++) {
        const c = curve.getPointAt(i / segs), r = r0 + (r1 - r0) * (i / segs);
        for (let j = 0; j <= 6; j++) { const v = i * 7 + j; p.setXYZ(v, c.x + (p.getX(v) - c.x) * r, c.y + (p.getY(v) - c.y) * r, c.z + (p.getZ(v) - c.z) * r); }
      }
      g.computeVertexNormals(); add(g, mat);
    };
    const rune = (pts: number[][], z: number, side: number, mat = energy) => {
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = new THREE.Vector3(pts[i][0], pts[i][1], z), b = new THREE.Vector3(pts[i + 1][0], pts[i + 1][1], z), d = b.clone().sub(a);
        const g = new THREE.BoxGeometry(0.014, d.length() + 0.012, 0.012);
        g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize()));
        a.add(b).multiplyScalar(0.5); add(g, mat, a.x, a.y, side * a.z);
      }
    };

    // plinth: two rough stone steps with a bronze rim and a ring of small rune stones
    add(hewn(new THREE.CylinderGeometry(0.36, 0.42, 0.12, 8), 0.035, seed), this.stone, 0, 0.06, 0);
    add(hewn(new THREE.CylinderGeometry(0.29, 0.33, 0.08, 8), 0.03, seed + 1), this.stone, 0, 0.16, 0);
    ring(0.335, 0.012, 0.125, M.bronze);
    for (let k = 0; k < 6; k++) {
      const a = k * Math.PI / 3 + 0.3;
      add(hewn(new THREE.BoxGeometry(0.07, 0.09, 0.05), 0.02, seed + 10 + k), this.stone, Math.sin(a) * 0.43, 0.045, Math.cos(a) * 0.43, 0, a);
      add(new THREE.BoxGeometry(0.012, 0.045, 0.006), energy, Math.sin(a) * 0.455, 0.05, Math.cos(a) * 0.455, 0, a);
    }

    if (wind) {
      // a slender hewn monolith, tapering, bound twice in bronze
      add(hewn(new THREE.CylinderGeometry(0.1, 0.17, 1.05, 6, 3), 0.03, seed + 2), this.stone, 0, 0.72, 0);
      ring(0.16, 0.016, 0.34, M.bronze); ring(0.115, 0.014, 1.12, M.bronze);
      // carved wind runes front and back: three rising chevrons and a spiral tick
      for (const side of [-1, 1]) for (const y of [0.52, 0.66, 0.8]) rune([[-0.045, y - 0.025], [0, y + 0.012], [0.045, y - 0.025]], 0.15 - (y - 0.5) * 0.06, side);
      // three bronze talons rise from the top and curl round the heart
      for (let k = 0; k < 3; k++) {
        const a = k * Math.PI * 2 / 3, c = Math.cos(a), sn = Math.sin(a);
        tube([[c * 0.07, 1.2, sn * 0.07], [c * 0.2, 1.33, sn * 0.2], [c * 0.2, 1.52, sn * 0.2], [c * 0.09, 1.66, sn * 0.09]], 0.026, 0.008, M.bronze, 12);
      }
      // a crest of bone feathers fanned behind the heart
      for (let k = -3; k <= 3; k++) {
        const g = new THREE.ShapeGeometry(leaf(0.46 - Math.abs(k) * 0.05, 0.12), 4);
        add(g, this.feather, 0, 1.34, -0.13 - Math.abs(k) * 0.01, -0.35, 0, k * 0.27);
        // the quill: a thin bronze shaft down each feather's middle
        add(new THREE.BoxGeometry(0.008, 0.3 - Math.abs(k) * 0.03, 0.008), M.bronze, -Math.sin(k * 0.27) * 0.15, 1.34 + Math.cos(k * 0.27) * 0.14, -0.08 - Math.abs(k) * 0.01, -0.35, 0, k * 0.27);
      }
      // a bundle of smaller feathers bound low on the shaft, hanging from the lower band
      for (let k = 0; k < 5; k++) {
        const a = k * 1.26 + 0.4, g = new THREE.ShapeGeometry(leaf(0.2, 0.05), 3);
        add(g, this.feather, Math.sin(a) * 0.15, 0.34, Math.cos(a) * 0.15, Math.PI - 0.35, a, 0);
      }
      // ribbons knotted under the talons, fluttering in the wind
      for (const [x, z, len] of [[0.11, 0.07, 0.62], [-0.1, 0.09, 0.54], [0.02, -0.12, 0.58], [-0.04, 0.12, 0.48]] as const) {
        const g = new THREE.PlaneGeometry(0.06, len, 1, 10); g.translate(0, -len / 2, 0);
        const p = g.attributes.position, w = new Float32Array(p.count);
        for (let i = 0; i < p.count; i++) w[i] = -p.getY(i) / len;
        g.setAttribute('aSway', new THREE.Float32BufferAttribute(w, 1));
        add(g, this.ribbon, x, 1.18, z, 0, Math.atan2(x, z));
      }
    } else if (kind === 'might') {
      // a squat, heavy block bound with three iron bands
      add(hewn(new THREE.BoxGeometry(0.42, 0.78, 0.36, 2, 3, 2), 0.04, seed + 2), this.stone, 0, 0.59, 0);
      for (const y of [0.3, 0.6, 0.9]) add(new THREE.BoxGeometry(0.45, 0.045, 0.39), M.iron, 0, y, 0);
      for (const y of [0.3, 0.6, 0.9]) for (const x of [-0.19, 0.19]) for (const z of [-0.18, 0.18]) add(new THREE.SphereGeometry(0.017, 6, 4), M.bronze, x, y, z);
      // carved might runes: a bold diamond with a down-stroke, front and back
      for (const side of [-1, 1]) {
        rune([[0, 0.83], [-0.07, 0.72], [0, 0.61], [0.07, 0.72], [0, 0.83]], 0.186, side);
        rune([[0, 0.58], [0, 0.4]], 0.186, side); rune([[-0.05, 0.47], [0.05, 0.47]], 0.186, side);
      }
      // an iron brazier bowl on top holds the fire heart
      add(new THREE.LatheGeometry([[0.06, 0], [0.2, 0.05], [0.25, 0.16], [0.27, 0.2], [0.24, 0.19], [0.18, 0.08], [0.06, 0.035]].map(([x, y]) => new THREE.Vector2(x, y)), 18), M.iron, 0, 0.99, 0);
      ring(0.262, 0.014, 1.19, M.bronze);
      // great ram horns of bone curling out of the block's shoulders
      for (const sd of [-1, 1]) {
        tube([[sd * 0.17, 0.95, -0.02], [sd * 0.34, 1.12, -0.12], [sd * 0.5, 1.12, 0.0], [sd * 0.52, 0.94, 0.12], [sd * 0.42, 0.82, 0.16], [sd * 0.36, 0.9, 0.2], [sd * 0.4, 1.0, 0.24]], 0.07, 0.014, this.bone, 26);
        // bone tooth charms hanging off the top band
        for (const z of [-0.12, 0.0, 0.12]) add(new THREE.ConeGeometry(0.018, 0.09, 5), this.bone, sd * 0.23, 0.86, z, Math.PI);
      }
    }
    if (kind === 'ascend') {
      // three stepped tiers of hewn stone, each smaller, like a stair to the sky
      for (const [y, w, h, d] of [[0.37, 0.4, 0.3, 0.4], [0.69, 0.32, 0.3, 0.32], [0.97, 0.24, 0.26, 0.24]] as const) {
        add(hewn(new THREE.BoxGeometry(w, h, d, 2, 2, 2), 0.03, seed + 30 + y * 10), this.stone, 0, y, 0);
        add(new THREE.BoxGeometry(w + 0.03, 0.03, d + 0.03), M.bronze, 0, y + h / 2 - 0.005, 0);
      }
      // a bronze stair-band spiralling up round the tiers
      const helix: number[][] = [];
      for (let j = 0; j <= 16; j++) { const a = j / 16 * Math.PI * 3.2, r = 0.29 - j * 0.009; helix.push([Math.cos(a) * r, 0.24 + j * 0.06, Math.sin(a) * r]); }
      tube(helix, 0.016, 0.009, M.bronze, 48);
      // side runes: an upward arrow each side
      for (const side of [-1, 1]) {
        const g = new THREE.BoxGeometry(0.012, 0.2, 0.012); add(g, energy, side * 0.205, 0.4, 0);
        add(new THREE.BoxGeometry(0.012, 0.06, 0.012), energy, side * 0.205, 0.49, 0.025, 0.8);
        add(new THREE.BoxGeometry(0.012, 0.06, 0.012), energy, side * 0.205, 0.49, -0.025, -0.8);
      }
      // ladder frame: two bronze rails beside the charge rungs
      for (const [y0, y1, z, w] of [[0.24, 0.5, 0.205, 0.09], [0.55, 0.82, 0.165, 0.075], [0.86, 1.08, 0.125, 0.06]] as const)
        for (const sd of [-1, 1]) add(new THREE.BoxGeometry(0.014, y1 - y0, 0.014), M.bronze, sd * (w + 0.01), (y0 + y1) / 2, z);
      // crown: an open bronze cup of five rising prongs round the heart, a halo ring above
      add(new THREE.CylinderGeometry(0.13, 0.09, 0.08, 10, 1, true), M.bronze, 0, 1.13, 0);
      for (let k = 0; k < 5; k++) {
        const a = k * Math.PI * 2 / 5, c = Math.cos(a), sn = Math.sin(a);
        tube([[c * 0.12, 1.15, sn * 0.12], [c * 0.2, 1.3, sn * 0.2], [c * 0.17, 1.52, sn * 0.17], [c * 0.13, 1.6, sn * 0.13]], 0.016, 0.006, M.bronze, 10);
      }
      add(new THREE.TorusGeometry(0.2, 0.008, 5, 32), M.bronze, 0, 1.68, 0, Math.PI / 2);
    }
    if (kind === 'shatter') {
      // a monolith split in two: the halves lean apart, a void heart hangs in the cleft between them
      for (const sd of [-1, 1]) {
        add(hewn(new THREE.BoxGeometry(0.17, 0.98, 0.3, 2, 4, 2), 0.035, seed + 40 + sd), this.stone, sd * 0.12, 0.69, 0, 0, 0, -sd * 0.1);
        // an iron clamp that failed to hold each half, and a jagged crack rune down its face
        add(new THREE.BoxGeometry(0.2, 0.04, 0.33), M.iron, sd * 0.14, 0.44, 0, 0, 0, -sd * 0.1);
        for (const side of [-1, 1]) rune([[sd * 0.1, 1.0], [sd * 0.14, 0.88], [sd * 0.09, 0.78], [sd * 0.15, 0.64], [sd * 0.1, 0.5]], 0.155, side);
        // broken bone spikes jutting from each shoulder toward the heart
        for (const [y, z] of [[1.14, -0.07], [1.08, 0.08]] as const) add(new THREE.ConeGeometry(0.022, 0.2, 5), this.bone, sd * 0.2, y, z, 0, 0, sd * 0.9);
      }
      // shards of the split hang frozen in the air above the cleft
      for (let k = 0; k < 4; k++) add(hewn(new THREE.TetrahedronGeometry(0.045 + k * 0.006), 0.01, seed + 50 + k), this.stone, (k - 1.5) * 0.09, 1.52 + (k % 2) * 0.06, (k % 2 ? 0.04 : -0.05), k, k * 0.7, 0);
      // a broken bronze ring round the base of the cleft
      add(new THREE.TorusGeometry(0.19, 0.012, 5, 24, Math.PI * 1.6), M.bronze, 0, 1.17, 0, Math.PI / 2, 0, 0.6);
    }
    return new Map(Array.from(parts, ([material, geometries]) => {
      const merged = mergeGeometries(geometries)!; geometries.forEach(g => g.dispose());
      return [material, merged] as const;
    }));
  }

  /** Bake every owner's current model into the per-material static meshes (rare: on equip). */
  private rebuildBodies() {
    for (const [mat, mesh] of this.statics) {
      const geos: THREE.BufferGeometry[] = [];
      this.views.forEach((v, i) => {
        const src = v.kind && this.bodies[v.kind].get(mat); if (!src) return;
        const p = this.world.totemSockets[i], g = src.clone().translate(p.x, p.y, p.z);
        if (mat === this.energy) {
          const c = _c.set(TOTEMS[v.kind!].color), n = g.attributes.position.count, col = new Float32Array(n * 3);
          for (let k = 0; k < n; k++) col.set([c.r, c.g, c.b], k * 3);
          g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
        }
        geos.push(g);
      });
      mesh.geometry.dispose();
      mesh.geometry = geos.length ? mergeGeometries(geos)! : new THREE.BufferGeometry();
      geos.forEach(g => g.dispose());
      mesh.geometry.computeBoundingSphere();
      mesh.visible = geos.length > 0;
    }
    this.used = new Set(this.views.map(v => v.kind));
  }
  /** Kinds some owner has equipped: per-kind extras (wisps, feathers, rungs) draw only for these. */
  private used = new Set<TotemKind | null>();

  pick(ray: THREE.Ray) {
    let best = -1, distance = Infinity;
    for (let i = 0; i < this.views.length; i++) if (ray.intersectBox(this.views[i].bounds, _hit)) {
      const d = ray.origin.distanceToSquared(_hit); if (d < distance) { best = i; distance = d; }
    }
    return best;
  }
  hoverAt(index: number) { this.hover = index; }
  activate(index: number) {
    if (index === 0) { this.chooser.hidden = !this.chooser.hidden; this.buttons[0].setAttribute('aria-expanded', String(!this.chooser.hidden)); this.syncChoices(); }
    else if (index === 1 && this.views[1]) { this.close(); this.board.inspectTotem(1); }
  }
  close(restoreFocus = false) {
    const wasOpen = !this.chooser.hidden;
    this.chooser.hidden = true; this.buttons[0].setAttribute('aria-expanded', 'false');
    if (wasOpen && restoreFocus) this.buttons[0].focus({ preventScroll: true });
  }
  /** The ally's label shows their nick instead of «СОЮЗНИК». */
  setAllyName(name: string) {
    const owner = this.buttons[1]?.querySelector('.totem-owner');
    if (owner) { owner.textContent = name || 'СОЮЗНИК'; owner.classList.toggle('totem-owner-nick', !!name); }
  }
  consumeShadowChange() { const dirty = this.shadowsChanged; this.shadowsChanged = false; return dirty; }
  private syncChoices() {
    for (const button of this.choices) {
      const active = button.dataset.kind === this.board.totems.kind; button.setAttribute('aria-pressed', String(active));
      button.querySelector('.totem-option-mark')!.textContent = active ? '✓' : '';
    }
  }

  update(camera: THREE.Camera, dt: number) {
    const t = fxUniforms.uTime.value;
    let rebuild = false;
    for (let i = 0; i < this.views.length; i++) {
      const v = this.views[i], p = this.world.totemSockets[i], kind = v.loadout.kind, spec = TOTEMS[kind], wind = kind === 'wind';
      if (v.version !== v.loadout.version) {
        v.kind = kind; v.version = v.loadout.version; v.reveal = 1; this.shadowsChanged = true; rebuild = true;
        this.ctx.glows.setColor(v.light, spec.color, 0.3); this.ctx.floorGlows.setColor(v.ground, spec.color, 0.22);
        const button = this.buttons[i]; button.dataset.totem = kind; button.style.setProperty('--totem', `#${spec.color.toString(16).padStart(6, '0')}`);
        button.title = `${i ? 'Тотем союзника' : 'Вибрати тотем'} · ${spec.name}\n${i ? spec.detail.replaceAll('ваших', 'союзних') : spec.detail}`;
        button.setAttribute('aria-label', `${i ? 'Союзник' : 'Ваш тотем'}. ${spec.name}. ${spec.badge}.${i ? '' : ' Натисніть, щоб замінити.'}`);
        button.querySelector('.totem-name')!.textContent = SHORT[kind]; button.querySelector('.totem-badge')!.textContent = spec.badge; v.badge = '';
        this.syncChoices();
      }
      v.focus += ((this.hover === i ? 1 : 0) - v.focus) * Math.min(1, dt * 12); v.reveal = Math.max(0, v.reveal - dt * 2);
      const phase = t + i * 2.4, pulse = wind ? 0.96 + Math.sin(phase * 1.3) * 0.07 : 0.92 + Math.pow(Math.max(0, Math.sin(phase * 1.8)), 4) * 0.15;
      const glow = pulse * (1 + v.focus * 0.35 + v.reveal * 0.35), H = HEART[kind];
      const ascend = kind === 'ascend', charge = v.loadout.progress;
      const cy = p.y + H.y + (wind ? Math.sin(phase * 1.2) * 0.025 : ascend ? Math.sin(phase * 1.5) * 0.03 + charge * 0.05 : Math.sin(phase * 0.9) * 0.01);
      // the heart: a living orb hovering in its talons / brazier, turning slowly
      _q.setFromEuler(_e.set(0.25, phase * (wind ? 0.5 : 0.2), 0));
      this.cores.set(i, _m.compose(_p.set(p.x, cy, p.z), _q, _s.setScalar(H.r * (1 + v.reveal * 0.15))));
      RelicPool.shade(this.cores, i, _c.set(spec.color), (ascend || kind === 'shatter' ? 0.8 + charge * 0.6 : 1.05) * glow, ascend ? 2 + Math.floor(charge * 3) : 3, v.reveal * 1.5 + v.focus * 0.4, i * 0.37, H.element, v.focus);
      // wind: faint spiral wisps of air wheel round the crown; might has none
      for (let k = 0; k < 2; k++) {
        _q.setFromEuler(_e.set(Math.PI / 2 + 0.35 * (k ? -1 : 1), phase * (k ? -0.9 : 0.9), 0.2));
        this.vanes.set(i * 2 + k, _m.compose(_p.set(p.x, cy - 0.05 + k * 0.08, p.z), _q, _s.setScalar(wind ? 0.3 + k * 0.06 : 0)));
        this.vanes.color(i * 2 + k, _c.set(spec.color).multiplyScalar(0.5 + v.focus * 0.2));
      }
      // drifting motes: bone feathers caught in the wind (wind) / none for might — it sheds embers instead
      for (let k = 0; k < 3; k++) {
        const a = phase * 0.6 + k * Math.PI * 2 / 3, rise = ((phase * 0.12 + k / 3) % 1 + 1) % 1;
        _q.setFromEuler(_e.set(Math.sin(phase * 2 + k) * 0.6, a * 1.3, Math.cos(phase * 1.7 + k) * 0.5));
        const sc = wind ? Math.sin(rise * Math.PI) * 0.11 : 0;
        this.motes.set(i * 3 + k, _m.compose(_p.set(p.x + Math.cos(a) * (0.32 + rise * 0.15), p.y + 0.9 + rise * 0.9, p.z + Math.sin(a) * (0.32 + rise * 0.15)), _q, _s.set(sc, sc, sc)));
        this.motes.color(i * 3 + k, _c.set(0xffffff));
      }
      if (kind === 'might' && this.fx && (this.emberT -= dt / this.views.length) <= 0) {
        // the brazier breathes fire and sheds embers round the fire heart
        this.emberT = 0.09;
        const a = Math.random() * Math.PI * 2, r = Math.random() * 0.14;
        this.fx.el.fire(p.x + Math.cos(a) * r, p.y + 1.2, p.z + Math.sin(a) * r, 0, 0.5 + Math.random() * 0.3, 0, 0.09, 0.2, 0.45, 0xff7a2a, 1.1, 0.35);
        if (Math.random() < 0.4) this.fx.p.emit({ x: p.x, y: p.y + 1.25, z: p.z, vx: (Math.random() - 0.5) * 0.4, vy: 0.6 + Math.random() * 0.5, vz: (Math.random() - 0.5) * 0.4,
          color: _c.set(0xffb060), intensity: 2.2, life: 1.1, size: 0.025, sizeEnd: 0, gravity: -0.2, drag: 0.8, shape: Shape.Glow, fade: 1 });
      }
      // ascend: the rungs light one by one as the charge fills; the lit top one breathes, a full ladder throbs
      const lit = charge * RUNGS.length, full = charge >= 1;
      RUNGS.forEach(([y, z, w], k) => {
        const f = ascend ? Math.min(1, Math.max(0, lit - k)) : 0;
        this.rungs.set(i * RUNGS.length + k, _m.compose(_p.set(p.x, p.y + y, p.z + z), _q.identity(), _s.set(ascend ? w : 0, 1, 1)));
        const b = full ? 0.85 + Math.sin(phase * 6) * 0.15 : f >= 1 ? 1 : 0.12 + f * 0.6 * (0.8 + Math.sin(phase * 5) * 0.2);
        this.rungs.color(i * RUNGS.length + k, _c.set(spec.color).multiplyScalar(b));
      });
      if (ascend && this.fx && (this.moteT -= dt) <= 0) {
        // golden motes drift up the spiral, quicker as the charge fills
        this.moteT = 0.32 - charge * 0.2;
        const a = phase * 1.4 + Math.random() * 0.6;
        this.fx.p.emit({ x: p.x + Math.cos(a) * 0.3, y: p.y + 0.25, z: p.z + Math.sin(a) * 0.3, vx: -Math.sin(a) * 0.25, vy: 0.55 + charge * 0.4, vz: Math.cos(a) * 0.25,
          color: _c.set(0xffe2a0), intensity: 1.6 + charge, life: 1.6, size: 0.02, sizeEnd: 0, drag: 0.4, shape: Shape.Glow, fade: 1 });
      }
      if (ascend && i === 0) {
        const left = Math.ceil(v.loadout.period * (1 - charge)), text = full ? 'готово · немає цілі' : `+1 рівень · ${left} с`;
        if (v.badge !== text) { v.badge = text; this.buttons[i].querySelector('.totem-badge')!.textContent = text; }
      }
      const shatter = kind === 'shatter';
      if (shatter && i === 0) {
        const left = Math.ceil(v.loadout.period * (1 - charge)), text = full ? 'готово!' : `розбити · ${left} с`;
        if (v.badge !== text) { v.badge = text; this.buttons[i].querySelector('.totem-badge')!.textContent = text; this.buttons[i].toggleAttribute('data-ready', full); }
      }
      if (shatter && full && this.fx && (this.moteT -= dt) <= 0) {
        // charged: crimson motes are sucked into the void heart
        this.moteT = 0.12;
        const a = Math.random() * Math.PI * 2, r = 0.45;
        this.fx.p.emit({ x: p.x + Math.cos(a) * r, y: cy + (Math.random() - 0.5) * 0.4, z: p.z + Math.sin(a) * r, vx: -Math.cos(a) * r * 1.6, vy: 0, vz: -Math.sin(a) * r * 1.6,
          color: _c.set(spec.color), intensity: 2, life: 0.6, size: 0.025, sizeEnd: 0, shape: Shape.Spark, stretch: 0.02, fade: 1 });
      }
      this.ctx.glows.setOpacity(v.light, 0.28 + v.focus * 0.15 + v.reveal * 0.12 + (ascend || kind === 'shatter' ? charge * 0.15 : 0));
      this.ctx.floorGlows.setOpacity(v.ground, 0.2 + v.focus * 0.12);
      // the label under the stone's foot; on a phone up on the plinth, clear of the skill medallions below
      _p.set(p.x, p.y + 0.04, p.z + (this.width < 560 ? -0.05 : 0.3)).project(camera);
      const x = Math.max(42, Math.min(this.width - 42, (_p.x + 1) * this.width / 2)), y = (1 - _p.y) * this.height / 2;
      const button = this.buttons[i], position = `${Math.round(x)},${Math.round(y)}`;
      if (button.dataset.position !== position) { button.style.left = Math.round(x) + 'px'; button.style.top = Math.round(y) + 'px'; button.dataset.position = position; }
      if (i === 0 && !this.chooser.hidden) {
        this.chooser.style.left = `${Math.max(8, Math.min(this.width - 236, x + 42))}px`;
        this.chooser.style.top = `${Math.max(8, Math.min(this.height - 268, y - 110))}px`;
      }
    }
    if (rebuild) this.rebuildBodies();
    this.cores.flush(); this.vanes.flush(); this.motes.flush(); this.rungs.flush();
    // (flush shows any pool with live slots; the extras of an unequipped kind stay hidden)
    if (!this.used.has('wind')) this.vanes.mesh.visible = this.motes.mesh.visible = false;
    if (!this.used.has('ascend')) this.rungs.mesh.visible = false;
  }
}
